import { useState, useEffect } from "react";
import { Form } from "react-final-form";
import { connect } from "react-redux";

import { makeApiCall } from "@lib/api/helpers";
import { getOffersRCAApi, paymentInsurranceApi } from "@lib/api";
import { addAlert } from "@store/app/actions";
import Button from "@components/Button";
import Alert from "@components/Alert";
import PolicyDeliverySection from "@containers/RCA/RCAProprietar/PolicyDeliverySection";
import Heading from "@components/Heading";
import RcaOffersPolicyInfo from "@containers/RCA/RcaOffers/RcaOffersPolicyInfo";
import RcaOffersPolicyType from "@containers/RCA/RcaOffers/RcaOffersPolicyType";
import RcaOffersList from "@containers/RCA/RcaOffers/RcaOffersList";

const Divider = () => (
  <div className="bg-white -mx-[15px] lg:-mx-[20px] h-[20px]" />
);

const RCAOferte = ({ formData, addAlert, apiData, onLoadingStateChange }) => {
  const [offers, setOffers] = useState(null);

  useEffect(() => {
    const fetchOffers = async () => {
      onLoadingStateChange(true);

      const response = await makeApiCall({
        apiCall: getOffersRCAApi,
        apiArgs: [apiData.orderId],
        returnResponse: true,
        formatResponse: (response) => response.data.data,
        preFail: (response) => {
          addAlert({
            message:
              response.data?.message ||
              "A aparut o eroare la incarcarea ofertelor. Va rugam incercati din nou.",
            variant: "error",
          });
        },
      });

      if (response?.items) {
        setOffers(response.items);
      }

      onLoadingStateChange(false);
    };

    if (apiData.orderId && !offers) {
      fetchOffers();
    }
  }, [apiData.orderId]);

  // Group offers by company
  const groupedOffers =
    offers &&
    offers.reduce((acc, offer) => {
      const companyName = offer.productDetails.vendorDetails.commercialName;

      if (!acc[companyName]) {
        acc[companyName] = {
          companyName,
          logoUrl: offer.productDetails.vendorDetails.linkLogo,
          offers: [],
        };
      }

      acc[companyName].offers.push(offer);

      if (acc[companyName].offers.length) {
        acc[companyName].offers.sort((a, b) => {
          return a.offerDetails.periodMonths - b.offerDetails.periodMonths;
        });
      }

      return acc;
    }, {});

  const handleSubmitOffer = async (values) => {
    const { selectedOffer } = values;

    if (!selectedOffer) {
      addAlert({
        message: "Va rugam selectati o oferta",
        variant: "warning",
      });
      return;
    }

    onLoadingStateChange(true);

    const payload = {
      internal_order_id: apiData.orderId,
      external_offer_id: Number(selectedOffer),
    };

    const response = await makeApiCall({
      apiCall: paymentInsurranceApi,
      apiArgs: [payload],
      formatResponse: (response) => response.data.data,
      returnResponse: true,
      preFail: (response) => {
        addAlert({
          message:
            response.data?.message ||
            "A aparut o eroare la procesarea platii. Va rugam incercati din nou.",
          variant: "error",
        });
      },
    });

    const paymentLink = response?.payment_link;

    if (paymentLink) {
      addAlert({
        message: "Redirectionare catre pagina de plata...",
        variant: "info",
      });

      window.location.href = paymentLink;
    }

    onLoadingStateChange(false);
  };

  if (!groupedOffers) {
    return null;
  }

  if (groupedOffers && Object.keys(groupedOffers).length === 0) {
    return (
      <div className="p-[24px] bg-white">
        <Alert type="warning" showIcon>
          Nu am gasit oferte disponibile pentru datele introduse. Va rugam
          verificati informatiile si incercati din nou.
        </Alert>
      </div>
    );
  }

  return (
    <Form
      initialValues={{ withDirectSettlement: "Yes", selectedOffer: null }}
      onSubmit={handleSubmitOffer}
      render={({ values, handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <div className="text-sm pb-[20px]">
            <RcaOffersPolicyInfo formData={formData} />
          </div>

          <Divider />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px] py-[20px]">
            <RcaOffersPolicyType values={values} />
          </div>

          <Divider />

          <div className="space-y-[16px] -mx-[15px] lg:-mx-[20px] -pb-[20px] bg-white">
            <RcaOffersList values={values} groupedOffers={groupedOffers} />
          </div>

          <div className="py-[16px] text-[14px]">
            Pentru verificarea tarfilor de referinta, va rugam accesati adresa{" "}
            <a
              className="text-red-brand font-medium hover:text-bleu-hover"
              href="https://www.baar.ro/asigurati-cu-risc-ridicat/"
              target="_blank"
              title="Tarife de referinta"
              rel="noopener noreferrer"
            >
              https://www.baar.ro/asigurati-cu-risc-ridicat/
            </a>
          </div>

          <Divider />

          <div className="space-y-[20px] py-[20px]">
            <Heading
              as="h4"
              display="h2"
              sameSizeOnMobile
              title="Livrarea politei si prelucrarea datelor"
            />

            <div className="bg-white p-[16px]">
              <PolicyDeliverySection />
            </div>
          </div>

          <div className="pt-[20px] text-center -mx-[15px] lg:-mx-[20px] -mb-[20px] bg-white">
            <Button
              variant="secondary"
              type="submit"
              title="Spre plata"
              disabled={!values.selectedOffer}
              classesProp="w-full sm:max-w-[250px]"
            />
          </div>
        </form>
      )}
    />
  );
};

export default connect(null, { addAlert })(RCAOferte);
